import type {
  EmployeeCustomObjectConfig,
  EmployeeHistory,
  EmployeeHistoryParams,
  EmployeePageConfig,
  EmployeePageConfigType,
} from '@kizen/api/employee';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithParams, WithRequired } from './types';

import {
  getEmployeeCustomObjectConfig,
  getEmployeePageConfig,
  getEmployeeHistory,
} from '@kizen/api/employee';
import { getResource } from './resource';
import { curry1 } from './utils';

type EmployeeCustomObjectConfigQuery<TData = EmployeeCustomObjectConfig> =
  WithRequired<
    QueryObserverOptions<
      EmployeeCustomObjectConfig,
      DefaultError,
      TData,
      EmployeeCustomObjectConfig,
      [
        Resource<
          Readonly<['employee', 'mine', 'configs', 'custom-objects', string]>
        >,
      ]
    >,
    'queryFn' | 'queryKey'
  >;

type EmployeePageConfigQuery<
  TType extends EmployeePageConfigType = EmployeePageConfigType,
  TData = EmployeePageConfig<TType>,
> = WithRequired<
  QueryObserverOptions<
    EmployeePageConfig<TType>,
    DefaultError,
    TData,
    EmployeePageConfig<TType>,
    [Resource<Readonly<['employee', 'mine', 'configs', 'page', TType]>>]
  >,
  'queryFn' | 'queryKey'
>;

type EmployeeHistoryQuery<TData = EmployeeHistory> = WithRequired<
  QueryObserverOptions<
    EmployeeHistory,
    DefaultError,
    TData,
    EmployeeHistory,
    [Resource<Readonly<['employee', string, 'history']>, EmployeeHistoryParams>]
  >,
  'queryFn' | 'queryKey'
>;

export const employeeCustomObjectConfigQuery = <
  TData = EmployeeCustomObjectConfig,
>(
  axios: AxiosInstance,
  customObjectId: string,
  options: Omit<
    EmployeeCustomObjectConfigQuery<TData>,
    'queryFn' | 'queryKey'
  > = {}
): EmployeeCustomObjectConfigQuery<TData> => {
  const path = [
    'employee',
    'mine',
    'configs',
    'custom-objects',
    customObjectId,
  ] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getEmployeeCustomObjectConfig(axios, qk.path[4], { signal });
    },
    ...options,
  };
};

export const employeePageConfigQuery = <
  T extends EmployeePageConfigType = EmployeePageConfigType,
  TData = EmployeePageConfig<T>,
>(
  axios: AxiosInstance,
  type: T,
  options: Omit<EmployeePageConfigQuery<T, TData>, 'queryFn' | 'queryKey'> = {}
): EmployeePageConfigQuery<T, TData> => {
  const path = ['employee', 'mine', 'configs', 'page', type] as const;
  const resource = getResource(path);

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getEmployeePageConfig(axios, qk.path[4], { signal });
    },
    ...options,
  };
};

export const employeeHistoryQuery = <TData = EmployeeHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<EmployeeHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    EmployeeHistoryParams
  > = {}
): EmployeeHistoryQuery<TData> => {
  const path = ['employee', id, 'history'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getEmployeeHistory(axios, qk.path[1], {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

class Queries {
  public employeeCustomObjectConfigQuery: <TData = EmployeeCustomObjectConfig>(
    ...args: Drop<Parameters<typeof employeeCustomObjectConfigQuery<TData>>>
  ) => EmployeeCustomObjectConfigQuery<TData>;
  public employeePageConfigQuery: <
    TType extends EmployeePageConfigType = EmployeePageConfigType,
    TData = EmployeePageConfig<TType>,
  >(
    ...args: Drop<Parameters<typeof employeePageConfigQuery<TType, TData>>>
  ) => EmployeePageConfigQuery<TType, TData>;
  public employeeHistoryQuery: <TData = EmployeeHistory>(
    ...args: Drop<Parameters<typeof employeeHistoryQuery<TData>>>
  ) => EmployeeHistoryQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.employeeCustomObjectConfigQuery = curry1(
      employeeCustomObjectConfigQuery
    )(axios);
    this.employeePageConfigQuery = curry1(employeePageConfigQuery)(axios);
    this.employeeHistoryQuery = curry1(employeeHistoryQuery)(axios);
  }
}

export type EmployeeQueries = Queries;

export const defEmployeeQueries = (axios: AxiosInstance): EmployeeQueries => {
  return new Queries(axios);
};
